import React from 'react';
import { useRecordContext, Button } from 'react-admin';
import { Card, CardContent, Typography, Box, Chip, Alert } from '@mui/material';
import {
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Assessment as QualityIcon,
} from '@mui/icons-material';
import { leadImportFields, leadImportStatuses } from '../../leadImports.model';
import auth from '../../../auth/auth0';
import { useApiConfig } from '../../../services/apiConfig';
import logger from '../../../utils/logger';
const MetricsDisplay = ({
  record: recordProp,
  variant = 'simple',
  ...props
}) => {
  const contextRecord = useRecordContext(props);
  const record = recordProp || contextRecord;
  const apiConfig = useApiConfig();

  if (!record) return null;


  const stats = record[leadImportFields.stats];


  const successful =
    stats?.successfulRows || record[leadImportFields.successfulRecords] || 0;
  const totalRows = stats?.totalRows || 0;
  const failedDetails = stats?.failedRows;
  const qualityAssessment = stats?.qualityAssessment;
  const hasDetailedReport = stats?.hasDetailedReport || false;
  const detailedReportUrl = stats?.detailedReportS3Url;


  const failedCount =
    failedDetails?.count || record[leadImportFields.failedRecords]?.count || 0;
  const duplicatedCount = record[leadImportFields.duplicatedRecords] || 0;
  const duplicatedFromCsvCount = record[leadImportFields.duplicateFromCsv] || 0;
  const duplicatedFromDbCount = record[leadImportFields.duplicateFromDb] || 0;
  const mergedCount = record[leadImportFields.mergedRecords] || 0;



  const total = totalRows || successful + failedCount;


  const successRate =
    qualityAssessment?.successRate ||
    (total > 0 ? ((successful / total) * 100).toFixed(1) : 0);
  const failureRate =
    qualityAssessment?.failureRate ||
    (total > 0 ? ((failedCount / total) * 100).toFixed(1) : 0);


  const quality = qualityAssessment?.quality;
  const warnings = qualityAssessment?.warnings || [];


  if (variant === 'simple') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span>
          {successful} successful
          {failedCount > 0 && `, ${failedCount} failed`}
          {duplicatedCount > 0 && `, ${duplicatedCount} duplicated`}
          {duplicatedFromCsvCount > 0 && `, ${duplicatedFromCsvCount} duplicated from CSV`}
          {duplicatedFromDbCount > 0 && `, ${duplicatedFromDbCount} duplicated from DB`}
          {mergedCount > 0 && `, ${mergedCount} merged`}
        </span>
        {quality && (
          <Chip
            label={quality.toUpperCase()}
            color={getQualityColor(quality)}
            size="small"
          />
        )}
      </Box>
    );
  }

  if (
    total === 0 &&
    record[leadImportFields.status] !== leadImportStatuses.COMPLETED
  ) {
    return null;
  }

  const handleDownloadDetailedReport = async () => {
    try {
      const response = await fetch(
        `${apiConfig.getApiUrl('leadImports')}/lead-imports/${record.id}/detailed-report`,
        {
          headers: {
            Authorization: `Bearer ${auth.getIdToken()}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        window.open(data.downloadUrl, '_blank');
      } else {
        logger.error('Failed to get detailed report download URL');
      }
    } catch (error) {
      logger.error('Error downloading detailed report:', error);
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <QualityIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            Processing Summary
          </Typography>
          {quality && (
            <Chip
              label={`Quality: ${quality.toUpperCase()}`}
              color={getQualityColor(quality)}
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        {/* Main metrics - Enhanced layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
            }}
          >
            <Typography variant="h4" color="text.primary" fontWeight="bold">
              {total.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Rows
            </Typography>
          </Box>

          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              border: '1px solid',
              borderColor: 'success.main',
              borderRadius: 1,
              bgcolor: 'success.50',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <SuccessIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {successful.toLocaleString()}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Successful ({successRate}%)
            </Typography>
          </Box>

          {failedCount > 0 && (
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                border: '1px solid',
                borderColor: 'error.main',
                borderRadius: 1,
                bgcolor: 'error.50',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {failedCount.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Failed ({failureRate}%)
              </Typography>
            </Box>
          )}

          {mergedCount > 0 && (
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                border: '1px solid',
                borderColor: 'info.main',
                borderRadius: 1,
                bgcolor: 'info.50',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <WarningIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {mergedCount.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Merged (Updated)
              </Typography>
            </Box>
          )}

          {duplicatedCount > 0 && (
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                border: '1px solid',
                borderColor: 'warning.main',
                borderRadius: 1,
                bgcolor: 'warning.50',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {duplicatedCount.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Duplicates (Skipped)
                {duplicatedFromCsvCount > 0 && ` • ${duplicatedFromCsvCount} CSV`}
                {duplicatedFromDbCount > 0 && ` • ${duplicatedFromDbCount} DB`}
              </Typography>
            </Box>
          )}

        </Box>

        {/* Quality Assessment Warnings */}
        {warnings && warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Quality Warnings:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {warnings.map((warning, index) => (
                <li key={index}>
                  <Typography variant="body2">{warning}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Quality indicators and actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Quality badges */}
            <Chip
              label={`Success Rate: ${successRate}%`}
              color={getSuccessRateColor(parseFloat(successRate))}
              size="small"
            />

            {parseFloat(failureRate) > 0 && (
              <Chip
                label={`Failure Rate: ${failureRate}%`}
                color={getFailureRateColor(parseFloat(failureRate))}
                size="small"
              />
            )}
          </Box>

          {/* Download detailed report */}
          {hasDetailedReport && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadDetailedReport}
            >
              Download Detailed Report
            </Button>
          )}
        </Box>

        {/* Additional processing info */}
        {(failedDetails?.isTruncated) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Available Details:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {failedDetails?.sample?.length > 0 && (
                <Typography variant="body2" color="error">
                  {failedDetails.sample.length} failed row details shown
                  {failedDetails.isTruncated && ` (sample of ${failedCount})`}
                </Typography>
              )}

            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Helper functions for color coding
const getQualityColor = (quality) => {
  switch (quality?.toLowerCase()) {
    case 'good':
      return 'success';
    case 'fair':
      return 'warning';
    case 'poor':
      return 'error';
    default:
      return 'default';
  }
};

const getSuccessRateColor = (rate) => {
  if (rate >= 95) return 'success';
  if (rate >= 85) return 'info';
  if (rate >= 70) return 'warning';
  return 'error';
};


const getFailureRateColor = (rate) => {
  if (rate < 2) return 'success';
  if (rate < 10) return 'warning';
  return 'error';
};

export default MetricsDisplay;
